interface Testimonial {
  quote: string;
  author: string;
  title?: string;
  image?: string;
  featured?: boolean;
}

export default function TestimonialsSection() {
  const testimonials: Testimonial[] = [
    {
      quote: "Forbes recognized Brooke as a natural connector who has a jellybean jar of creatively brilliant people in her life. She is a secret weapon for many visionaries, bestselling authors, and speakers who come to her for connections and business development.",
      author: "Forbes.com",
      title: "The Best Places To Network",
      image: "https://static.readdy.ai/image/28da5b82b62ca95ef0a33638ffb4027e/b6964fbd92e5f149d36d5cd4896ba958.png",
      featured: true
    },
    {
      quote: "Working with Brooke was total magic! She was able to help me create and launch online programs and support me from conception to execution. Brooke is also a super connector and has introduced me to many people who I now consider to be both colleagues and friends. I trust Brooke's insights and business acumen immensely and cannot recommend her highly enough!",
      author: "Esther Blum",
      title: "Integrative Dietitian, Bestselling Author",
      image: "https://static.readdy.ai/image/28da5b82b62ca95ef0a33638ffb4027e/eb72349df54e7597adfe73af76a2c46f.png"
    },
    {
      quote: "I found out as I got to know her better that she is one of the greatest connectors globally, doing business at a very high level with people making major waves in this world. When I launched a new company this year, my business partner and I immediately enrolled her to find the perfect partners for our product. Besides being impeccable, intuitive and gifted at all things collaboration, she has a great personality, is a leader AND a team player, and has a great sense of humor.",
      author: "Kylie Slavik",
      title: "Brand Storytelling & Digital Marketing",
      image: "https://static.readdy.ai/image/28da5b82b62ca95ef0a33638ffb4027e/d733d7ff86d60e3f6a7b5cfd7b892068.png"
    },
    {
      quote: "Brooke enthusiastically connected with more than 175 potential partners, contributing her infectious belief in infinite possibility in every conversation. Without exception, Brooke added value to the day of every new connection. By cart open, Brooke had secured partner commitments with mailings that reached approximately 953,097 inboxes. This critical component of our launch could not have been more successful.",
      author: "Erin Melnick",
      title: "COO for David Neagle"
    },
    {
      quote: "I have had the privilege of working with Brooke for over 20 years. Guided by spirit, Brooke has been instrumental in helping me manifest my goals and desires, no matter how ambitious they seemed. Her deep intuition, combined with her practical wisdom, has allowed me to overcome numerous obstacles and navigate challenging decision-making with clarity and confidence.",
      author: "Rachel Wey",
      title: ""
    },
    {
      quote: "Within the first 100 days of work with Brooke, we generated significant revenue increase, launched our rebranded multimedia website, recruited new students for our first online workshop, and created new products. Brooke's professional drive and practice is fearless, meticulous, high vibe, and was a magnet for my trust. Every recommendation, collaboration, and connection she's given me has worked out in a quantum leap way.",
      author: "Christine O'Leary",
      title: "christineoleary.com",
      image: "https://static.readdy.ai/image/28da5b82b62ca95ef0a33638ffb4027e/43fd47cd457d84c28818c1533a9b694c.png"
    },
    {
      quote: "Being a connector is one of the most generous acts of human kindness. Brooke, thank you again for your generosity.",
      author: "Michael Port",
      title: "NY Times & WSJ Bestselling Author of Steal the Show",
      image: "https://static.readdy.ai/image/28da5b82b62ca95ef0a33638ffb4027e/0b76b4b95eb2b0d3cf0d5c80cd6ed7d2.png"
    }
  ];

  const featuredTestimonial = testimonials.find(t => t.featured);
  const regularTestimonials = testimonials.filter(t => !t.featured);

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What People Say
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Over 20+ years, I've had the privilege of working with visionaries, bestselling authors, and world-changing companies. Here's what some of them have shared about our work together.
          </p>
        </div>

        {/* Featured Testimonial */}
        {featuredTestimonial && (
          <div className="mb-16 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 md:p-12 shadow-lg border border-amber-100">
            <div className="flex flex-col items-center text-center">
              {featuredTestimonial.image && (
                <div className="mb-6 w-32 h-32 flex items-center justify-center">
                  <img 
                    src={featuredTestimonial.image}
                    alt={featuredTestimonial.author}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <blockquote className="text-xl md:text-2xl text-gray-800 leading-relaxed mb-6 font-light">
                "{featuredTestimonial.quote}"
              </blockquote>
              <div className="text-lg font-semibold text-gray-900">
                — {featuredTestimonial.author}
              </div>
              {featuredTestimonial.title && (
                <div className="text-base text-gray-600 mt-1">
                  {featuredTestimonial.title}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Regular Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {regularTestimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
            >
              {testimonial.image && (
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                </div>
              )}
              <blockquote className="text-base text-gray-700 leading-relaxed mb-4">
                "{testimonial.quote}"
              </blockquote>
              <div className="border-t border-gray-200 pt-4">
                <div className="font-semibold text-gray-900 text-sm">
                  — {testimonial.author}
                </div>
                {testimonial.title && (
                  <div className="text-sm text-gray-600 mt-1">
                    {testimonial.title}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
